import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, isDevMode } from '@/lib/firebase';
import type { AdminUser } from '@/types';

interface AuthContextValue {
  user: AdminUser | null;
  loading: boolean;
  error: string | null;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const DEV_USER: AdminUser = {
  uid: 'dev-admin-001',
  email: 'admin@kombatix.io',
  displayName: 'Dev Admin',
  photoURL: null,
};

const ALLOWED_DOMAIN = 'kombatix.io';

function mapFirebaseUser(user: User): AdminUser {
  return {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    photoURL: user.photoURL,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(isDevMode ? DEV_USER : null);
  const [loading, setLoading] = useState(!isDevMode);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isDevMode || !auth) return;

    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const email = firebaseUser.email || '';
        if (email.endsWith(`@${ALLOWED_DOMAIN}`)) {
          setUser(mapFirebaseUser(firebaseUser));
          setError(null);
        } else {
          signOut(auth!);
          setUser(null);
          setError(`Access restricted to @${ALLOWED_DOMAIN} accounts.`);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsub;
  }, []);

  const loginWithGoogle = async () => {
    if (isDevMode) {
      setUser(DEV_USER);
      return;
    }
    if (!auth) return;
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ hd: ALLOWED_DOMAIN });
      const result = await signInWithPopup(auth, provider);
      const email = result.user.email || '';
      if (!email.endsWith(`@${ALLOWED_DOMAIN}`)) {
        await signOut(auth);
        setError(`Access restricted to @${ALLOWED_DOMAIN} accounts.`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Sign-in failed';
      setError(msg);
    }
  };

  const logout = async () => {
    if (isDevMode) {
      setUser(null);
      return;
    }
    if (auth) await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
